import { useEffect, useState } from 'react';
import { ClipboardCheck, BookOpen, FlaskConical, Sparkles, Users, ChevronRight, ArrowRight, type LucideIcon } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Step {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
}

const STEPS: Step[] = [
  {
    icon: ClipboardCheck,
    title: 'Realiza tu diagnóstico',
    description: 'Inicia respondiendo el diagnóstico para conocer en qué nivel de madurez en IA te encuentras hoy.',
    color: 'from-teal-500 to-cyan-600',
  },
  {
    icon: BookOpen,
    title: 'Explora los contenidos',
    description: 'Navega por las categorías de aprendizaje y comienza a formarte a tu ritmo.',
    color: 'from-blue-500 to-indigo-600',
  },
  {
    icon: FlaskConical,
    title: 'Identifica tu proyecto',
    description: 'Entra a la sección de Proyectos y usa el laboratorio para descubrir una iniciativa que impacte tu rol o tu empresa.',
    color: 'from-purple-500 to-fuchsia-600',
  },
  {
    icon: Sparkles,
    title: 'Construye con el asistente',
    description: 'Abre tu proyecto y deja que el asistente de prototipado te guíe paso a paso para construir tu solución de IA con contenido recomendado.',
    color: 'from-amber-500 to-orange-600',
  },
  {
    icon: Users,
    title: 'Conéctate con la comunidad',
    description: 'Explora tendencias, herramientas de IA y haz preguntas a otros miembros de la comunidad.',
    color: 'from-pink-500 to-rose-600',
  },
];

export default function WelcomeOnboardingModal() {
  const { user, profile, refreshProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile && !profile.onboarding_completed_at) {
      setOpen(true);
    }
  }, [profile]);

  const markCompleted = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      await supabase
        .from('profiles')
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq('id', user.id);
      await refreshProfile();
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) markCompleted(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <div className="p-6 space-y-6">
          <div className="flex justify-center gap-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`h-2 rounded-full transition-all ${
                  i === step ? 'w-8 bg-primary' : i < step ? 'w-2 bg-primary/60' : 'w-2 bg-muted-foreground/30'
                }`}
                aria-label={`Ir al paso ${i + 1}`}
              />
            ))}
          </div>

          <div className="flex justify-center">
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${current.color} flex items-center justify-center shadow-lg`}>
              <Icon className="w-10 h-10 text-white" />
            </div>
          </div>

          <div className="text-center space-y-3">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
              Paso {step + 1} de {STEPS.length}
            </span>
            <h2 className="text-2xl font-bold text-foreground">{current.title}</h2>
            <p className="text-muted-foreground leading-relaxed">{current.description}</p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={markCompleted}
              disabled={saving}
              className="flex-1 text-muted-foreground hover:text-foreground"
            >
              Saltar
            </Button>
            <Button
              onClick={() => (isLast ? markCompleted() : setStep((s) => s + 1))}
              disabled={saving}
              className="flex-1 gradient-primary"
            >
              {isLast ? (
                <>Comenzar<ArrowRight className="w-4 h-4 ml-2" /></>
              ) : (
                <>Siguiente<ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
